import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Select from 'react-select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FiX, FiSave, FiAlertCircle, FiClock, FiUsers, FiSettings, FiCalendar, FiPlus, FiTrash2, FiRepeat, FiCheckCircle } from 'react-icons/fi';

const minimalToolbar = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
    ],
};

const TicketSchedulerModal = ({ schedule, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        repeat: 'daily',
        daily: '',
        weekly: [],
        monthly: [],
        yearly: '',
        severity: '',
        assigned_to: [],
        start_date: new Date().toISOString().split('T')[0],
        execution_limit: '0',
        is_active: 1,
        tags: [],
        times: ['09:00'],
        timing: '0',
        cron: 'automation_ticket_creation',
        files: [],
        existingFiles: [],
        client: '',
        customer: '',
        module: '',
        issue_type: 'Others',
        enhancement_type: '',
        enhancement_hours: '0.00',
        repeat_interval: '1'
    });

    const [options, setOptions] = useState({
        statusOptions: [],
        severity: [],
        userRoles: [],
        tags: [],
        usersList: [],
        clientList: []
    });

    const [customerOptions, setCustomerOptions] = useState([]);
    const [moduleOptions, setModuleOptions] = useState([]);
    const [isCustomerLoading, setIsCustomerLoading] = useState(false);
    const [isModuleLoading, setIsModuleLoading] = useState(false);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const response = await api.get('/ticket-scheduler/options');
                setOptions(prev => ({ ...prev, ...response.data }));

                if (schedule) {
                    // Fetch existing files
                    try {
                        const filesRes = await api.get(`/ticket-scheduler/files/${schedule.schedule_id}`);

                        setFormData(prev => ({
                            ...prev,
                            title: schedule.title,
                            description: schedule.description,
                            repeat: schedule.daily ? 'daily' : (schedule.weekly ? 'weekly' : (schedule.monthly ? 'monthly' : 'yearly')),
                            daily: schedule.daily || '',
                            weekly: schedule.weekly ? schedule.weekly.split(',') : [],
                            monthly: schedule.monthly ? schedule.monthly.split(',') : [],
                            yearly: schedule.yearly || '',
                            severity: schedule.severity_id,
                            assigned_to: schedule.assigned_to ? schedule.assigned_to.split(',') : [],
                            start_date: schedule.start_date,
                            execution_limit: schedule.execution_limit || '0',
                            is_active: Number(schedule.is_active),
                            tags: schedule.tags ? schedule.tags.split(',') : [],
                            times: schedule.start_time ? schedule.start_time.split(',') : ['09:00'],
                            cron: schedule.cron_function || 'automation_ticket_creation',
                            existingFiles: filesRes.data || [],
                            client: schedule.client_name || '',
                            customer: schedule.customer_name || '',
                            module: schedule.module || '',
                            issue_type: schedule.issue_type || 'Others',
                            enhancement_type: schedule.enhancement_type || '',
                            enhancement_hours: schedule.enhancement_hours || '0.00',
                            repeat_interval: schedule.repeat_interval || '1'
                        }));
                    } catch (e) {
                        console.error("Error fetching files", e);
                    }
                }
            } catch (error) {
                toast.error('Failed to load options');
            }
        };
        fetchOptions();
    }, [schedule]);

    // Fetch customers when client changes
    useEffect(() => {
        if (formData.client) {
            const fetchCustomers = async () => {
                setIsCustomerLoading(true);
                try {
                    const res = await api.get('/ticket/get-customers', {
                        params: { cust_key: formData.client },
                    });
                    if (res.data.status) {
                        setCustomerOptions(res.data.data);
                    }
                } catch (error) {
                    console.error('Failed to fetch customers:', error);
                } finally {
                    setIsCustomerLoading(false);
                }
            };
            fetchCustomers();
        } else {
            setCustomerOptions([]);
        }
    }, [formData.client]);

    // Fetch modules when customer changes
    useEffect(() => {
        if (formData.customer) {
            const fetchModules = async () => {
                setIsModuleLoading(true);
                try {
                    // Find the selected customer to get their modules
                    const selectedCustomer = customerOptions.find(c => c.cust_key === formData.customer);
                    if (selectedCustomer?.modules) {
                        const modules = selectedCustomer.modules.map(mod => ({
                            value: mod.id,
                            label: mod.module_name,
                        }));
                        setModuleOptions(modules);
                    } else {
                        // Fallback to API if modules not in customer record
                        const res = await api.get('/ticket/get-modules', {
                            params: { child_id: formData.customer }
                        });
                        if (res.data.status) {
                            setModuleOptions(res.data.data.map(m => ({ value: m.id, label: m.module_name })));
                        }
                    }
                } catch (error) {
                    console.error('Failed to fetch modules:', error);
                } finally {
                    setIsModuleLoading(false);
                }
            };
            fetchModules();
        } else {
            setModuleOptions([]);
        }
    }, [formData.customer, customerOptions]);


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleQuillChange = (content) => {
        setFormData(prev => ({ ...prev, description: content }));
    };

    const handleMultiSelect = (name, values) => {
        setFormData(prev => ({ ...prev, [name]: values.map(v => v.value) }));
    };

    const handleAddTime = () => {
        setFormData(prev => ({ ...prev, times: [...prev.times, '09:00'] }));
    };

    const handleRemoveTime = (index) => {
        if (formData.times.length > 1) {
            const newTimes = formData.times.filter((_, i) => i !== index);
            setFormData(prev => ({ ...prev, times: newTimes }));
        }
    };

    const handleTimeChange = (index, value) => {
        const newTimes = [...formData.times];
        newTimes[index] = value;
        setFormData(prev => ({ ...prev, times: newTimes }));
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFormData(prev => ({ ...prev, files: [...prev.files, ...selectedFiles] }));
    };

    const removeFile = (index) => {
        setFormData(prev => ({
            ...prev,
            files: prev.files.filter((_, i) => i !== index)
        }));
    };

    const removeExistingFile = async (fileID) => {
        if (window.confirm('Are you sure you want to delete this file permanently?')) {
            try {
                await api.delete(`/ticket-scheduler/file-delete/${fileID}`);
                setFormData(prev => ({
                    ...prev,
                    existingFiles: prev.existingFiles.filter(f => f.id !== fileID)
                }));
                toast.success('File deleted');
            } catch (error) {
                toast.error('Failed to delete file');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const url = schedule
                ? `/ticket-scheduler/update/${schedule.schedule_id}`
                : '/ticket-scheduler/create';

            const data = new FormData();

            // Append all fields to FormData
            Object.keys(formData).forEach(key => {
                if (key === 'files') {
                    formData.files.forEach((file) => {
                        data.append('files[]', file);
                    });
                } else if (Array.isArray(formData[key])) {
                    data.append(key, formData[key].join(','));
                } else {
                    data.append(key, formData[key]);
                }
            });

            // Special handling for time slots array joined as 'time'
            data.append('time', formData.times.join(','));

            await api.post(url, data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success(`Schedule ${schedule ? 'updated' : 'created'} successfully`);
            onSave();
            onClose();
        } catch (error) {
            const msg = error.response?.data?.message || 'Failed to save schedule';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const weekDays = [
        { value: 'Mon', label: 'Mon' }, { value: 'Tue', label: 'Tue' },
        { value: 'Wed', label: 'Wed' }, { value: 'Thu', label: 'Thu' },
        { value: 'Fri', label: 'Fri' }, { value: 'Sat', label: 'Sat' },
        { value: 'Sun', label: 'Sun' }
    ];

    const monthDays = Array.from({ length: 31 }, (_, i) => ({ value: (i + 1).toString(), label: (i + 1).toString() }));

    const roleOptions = options.userRoles.map(r => ({ value: r.id, label: r.name }));
    const assigneeOptions = options.usersList.map(u => ({
        value: u.user_id,
        label: `${u.name} (${u.designation_name})`
    }));
    const priorityOptions = options.severity.map(s => ({ value: s.severity_id, label: s.severity }));

    const [isRecurrencePopupOpen, setIsRecurrencePopupOpen] = useState(false);

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden border border-gray-100 flex flex-col">
                <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
                    {/* Header */}
                    <div className="p-4 border-b flex justify-center items-center relative">
                        <h2 className="text-2xl font-bold text-red-600 tracking-tight">
                            {schedule ? 'Update Ticket Schedule' : 'Create New Ticket Schedule'}
                        </h2>
                        <p className="absolute bottom-0 text-[10px] text-gray-400 font-medium">
                            {schedule ? 'Modify automated support request criteria' : 'Fill in the details below to automate support requests'}
                        </p>
                        <button
                            type="button"
                            onClick={onClose}
                            className="absolute right-4 top-4 p-1.5 bg-black text-white rounded-full hover:bg-gray-800 transition-colors shadow-lg active:scale-90"
                        >
                            <FiX size={18} />
                        </button>
                    </div>

                    <div className="p-6 overflow-y-auto space-y-6 flex-grow custom-scrollbar">
                        {/* Section 1: Title & Description with Attachments */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-1.5">Ticket Title <span className="text-red-500">*</span></label>
                            <input
                                type="text" name="title" value={formData.title} onChange={handleInputChange} required
                                className="w-full h-[40px] px-4 border border-gray-300 rounded-lg focus:ring-1 focus:ring-red-500 outline-none transition-all"
                                placeholder="e.g. Weekly Server Maintenance Report"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                            <div className="flex flex-col">
                                <label className="block text-sm font-semibold text-gray-800 mb-1.5">Ticket Description <span className="text-red-500">*</span></label>
                                <div className="border border-gray-300 rounded-lg overflow-hidden flex flex-col" style={{ resize: 'vertical', minHeight: '150px', height: '150px' }}>
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.description}
                                        onChange={handleQuillChange}
                                        modules={minimalToolbar}
                                        className="bg-white flex-grow"
                                        placeholder="Enter instructions..."
                                        style={{ height: 'calc(100% - 42px)' }}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col">
                                <label className="block text-sm font-semibold text-gray-800 mb-1.5">Attachments</label>
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer relative min-h-[150px] h-[150px]">
                                    <input
                                        type="file"
                                        multiple
                                        onChange={handleFileChange}
                                        className="absolute inset-0 opacity-0 cursor-pointer z-10"
                                    />
                                    <div className="text-center relative z-0">
                                        <div className="flex justify-center mb-1">
                                            <div className="p-1 bg-white rounded-full shadow-sm">
                                                <FiPlus className="text-red-600 size-4" />
                                            </div>
                                        </div>
                                        <span className="text-xs font-semibold text-gray-700 block">Drag or click to upload</span>
                                        <span className="text-[9px] text-gray-500 mt-0.5 block max-w-[200px]">PDF, JPEG, PNG. Max: 2MB</span>
                                    </div>

                                    {(formData.files.length > 0 || formData.existingFiles.length > 0) && (
                                        <div className="mt-4 w-full space-y-2 relative z-20 overflow-y-auto max-h-[80px]">
                                            {/* Existing Files */}
                                            {formData.existingFiles.map((file) => (
                                                <div key={file.id} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200 text-xs shadow-sm">
                                                    <span className="truncate max-w-[150px] font-medium text-blue-600">{file.filepath.split('/').pop()}</span>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); removeExistingFile(file.id); }}
                                                        className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                                                    >
                                                        <FiTrash2 size={12} />
                                                    </button>
                                                </div>
                                            ))}

                                            {/* New Files */}
                                            {formData.files.map((file, idx) => (
                                                <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200 text-xs shadow-sm">
                                                    <span className="truncate max-w-[150px] font-medium text-gray-600">{file.name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                                                        className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                                                    >
                                                        <FiTrash2 size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Moved Ticket Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Client <span className="text-red-500">*</span></label>
                                <select
                                    name="client" value={formData.client}
                                    onChange={(e) => setFormData({ ...formData, client: e.target.value, customer: '', module: '' })}
                                    required
                                    className="w-full h-[38px] px-3 bg-white border border-gray-300 rounded hover:border-red-400 outline-none text-sm"
                                >
                                    <option value="">Select Client</option>
                                    {options.clientList.map(c => (
                                        <option key={c.cust_key} value={c.cust_key}>
                                            {c.short_code || c.customer_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center">
                                    Customer Name <span className="text-red-500 ml-1">*</span>
                                    {isCustomerLoading && <span className="animate-spin inline-block w-3 h-3 border-[2px] border-current border-r-transparent rounded-full ml-2"></span>}
                                </label>
                                <select
                                    name="customer" value={formData.customer}
                                    onChange={(e) => setFormData({ ...formData, customer: e.target.value, module: '' })}
                                    required
                                    className="w-full h-[38px] px-3 bg-white border border-gray-300 rounded hover:border-red-400 outline-none text-sm"
                                >
                                    <option value="">Select Customer</option>
                                    {customerOptions.map(cust => (
                                        <option key={cust.cust_key} value={cust.cust_key}>
                                            {cust.customer_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center">
                                    Module <span className="text-red-500 ml-1">*</span>
                                    {isModuleLoading && <span className="animate-spin inline-block w-3 h-3 border-[2px] border-current border-r-transparent rounded-full ml-2"></span>}
                                </label>
                                <select
                                    name="module" value={formData.module}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full h-[38px] px-3 bg-white border border-gray-300 rounded hover:border-red-400 outline-none text-sm"
                                >
                                    <option value="">Select Module</option>
                                    {moduleOptions.map(mod => (
                                        <option key={mod.value} value={mod.value}>
                                            {mod.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Issue Type <span className="text-red-500">*</span></label>
                                <select
                                    name="issue_type" value={formData.issue_type}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full h-[38px] px-3 bg-white border border-gray-300 rounded hover:border-red-400 outline-none text-sm"
                                >
                                    <option value="Bug">Bug</option>
                                    <option value="Enhancement">Enhancement</option>
                                    <option value="Others">Others</option>
                                </select>
                            </div>
                        </div>

                        {formData.issue_type === 'Enhancement' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 animate-in slide-in-from-top-2 duration-200">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Enhancement Type <span className="text-red-500">*</span></label>
                                    <select
                                        name="enhancement_type" value={formData.enhancement_type}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full h-[38px] px-3 bg-white border border-gray-300 rounded hover:border-red-400 outline-none text-sm"
                                    >
                                        <option value="">Select Enhancement Type</option>
                                        <option value="Billable">Billable</option>
                                        <option value="Non-Billable">Non-Billable</option>
                                    </select>
                                </div>
                                {formData.enhancement_type === 'Billable' && (
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Enhancement Hours <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <FiClock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                            <input
                                                type="number" step="0.01" name="enhancement_hours"
                                                value={formData.enhancement_hours}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full h-[38px] pl-10 pr-3 border border-gray-300 rounded hover:border-red-400 outline-none text-sm"
                                                placeholder="0.00"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Section 2: Assignment & Priority */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Assigned To <span className="text-red-500">*</span></label>
                                <Select
                                    isMulti
                                    options={assigneeOptions}
                                    value={assigneeOptions.filter(opt => formData.assigned_to.includes(opt.value.toString()))}
                                    onChange={(vals) => handleMultiSelect('assigned_to', vals)}
                                    className="text-sm react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Select Employees"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Priority <span className="text-red-500">*</span></label>
                                <select
                                    name="severity"
                                    value={formData.severity}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full h-[38px] px-3 bg-white border border-gray-300 rounded hover:border-red-400 focus:border-red-500 outline-none transition-all text-sm"
                                >
                                    <option value="">Select Priority</option>
                                    {options.severity.map(s => <option key={s.severity_id} value={s.severity_id}>{s.severity}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Section 3: Status & Recurrence */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5 text-gray-400">Status (Auto-set)</label>
                                <input
                                    type="text" disabled value="New"
                                    className="w-full h-[38px] px-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-500 font-medium"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Recurrence Type <span className="text-red-500">*</span></label>
                                <select
                                    name="repeat" value={formData.repeat}
                                    onChange={(e) => {
                                        handleInputChange(e);
                                        if (e.target.value !== 'daily') setIsRecurrencePopupOpen(true);
                                    }}
                                    className="w-full h-[38px] px-3 bg-white border border-gray-300 rounded hover:border-red-400 outline-none text-sm"
                                >
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="yearly">Yearly</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Start Date <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    <input
                                        type="date" name="start_date" value={formData.start_date} onChange={handleInputChange} required
                                        className="w-full h-[38px] pl-10 pr-3 border border-gray-300 rounded hover:border-red-400 outline-none text-sm"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Recurring Settings Button (only show if not daily) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Execution Limit (Runs)</label>
                                <input
                                    type="number" name="execution_limit" value={formData.execution_limit} onChange={handleInputChange}
                                    min="0" placeholder="0 for infinite"
                                    className="w-full h-[38px] px-3 border border-gray-300 rounded hover:border-red-400 outline-none text-sm"
                                />
                            </div>

                            {formData.repeat !== 'daily' && (
                                <div className="flex items-end">
                                    <button
                                        type="button"
                                        onClick={() => setIsRecurrencePopupOpen(true)}
                                        className="w-full h-[38px] bg-blue-50 text-blue-600 border border-blue-200 rounded-lg text-sm font-bold flex items-center justify-center gap-2 hover:bg-blue-100 transition-all active:scale-[0.98]"
                                    >
                                        <FiSettings className="animate-spin-slow" />
                                        Configure {formData.repeat.charAt(0).toUpperCase() + formData.repeat.slice(1)} Pattern
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Execution Times Section */}
                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                                    <FiClock className="text-red-600" /> EXECUTION TIMES
                                </label>
                                <button
                                    type="button"
                                    onClick={handleAddTime}
                                    className="text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-red-700 transition-all shadow-sm active:scale-95"
                                >
                                    <FiPlus /> Add Time Slot
                                </button>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
                                {formData.times.map((time, index) => (
                                    <div key={index} className="relative group">
                                        <input
                                            type="time"
                                            value={time}
                                            onChange={(e) => handleTimeChange(index, e.target.value)}
                                            className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-1 focus:ring-red-500 outline-none transition-all shadow-sm"
                                        />
                                        {formData.times.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveTime(index)}
                                                className="absolute -top-2 -right-2 bg-white text-red-500 rounded-full border border-red-100 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 shadow-sm"
                                            >
                                                <FiX size={12} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t bg-gray-50/50 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="is_active"
                                checked={Number(formData.is_active) === 1}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                                className="w-4 h-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                            />
                            <label htmlFor="is_active" className="text-sm font-medium text-gray-700 cursor-pointer">
                                Active Schedule
                            </label>
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="button" onClick={onClose}
                                className="px-8 py-2 text-gray-600 hover:bg-gray-200 rounded-lg font-bold transition-all border border-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit" disabled={loading}
                                className="px-10 py-2 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 shadow-xl active:scale-95 transition-all flex items-center gap-3 disabled:opacity-50"
                            >
                                {loading ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                ) : (
                                    <FiCheckCircle size={20} />
                                )}
                                {schedule ? 'Update Schedule' : 'Create Schedule'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            {/* Recurrence Popup */}
            {isRecurrencePopupOpen && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-200">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border border-gray-200 overflow-hidden transform animate-in zoom-in-95 duration-200">
                        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-blue-600 text-white rounded-lg shadow-md">
                                    <FiRepeat size={16} />
                                </div>
                                <h3 className="font-bold text-gray-800 tracking-tight">Recurring Schedule Details</h3>
                            </div>
                            <button
                                onClick={() => setIsRecurrencePopupOpen(false)}
                                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-200 rounded-full transition-all"
                            >
                                <FiX size={20} />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Repeat Interval */}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Repeat Interval</label>
                                <div className="flex items-center gap-3">
                                    <div className="flex-1 flex items-center gap-2 bg-gray-50 p-2 rounded-lg border border-gray-200">
                                        <span className="text-sm font-medium text-gray-400 pl-1">Every</span>
                                        <input
                                            type="number"
                                            name="repeat_interval"
                                            value={formData.repeat_interval}
                                            onChange={handleInputChange}
                                            min="1"
                                            className="w-16 h-8 text-center bg-white border border-gray-300 rounded font-bold text-red-600 outline-none focus:border-red-500"
                                        />
                                        <span className="text-sm font-bold text-gray-700">
                                            {formData.repeat === 'weekly' ? 'Week(s)' : formData.repeat === 'monthly' ? 'Month(s)' : 'Year(s)'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Specific selectors for Weekly/Monthly */}
                            {(formData.repeat === 'weekly' || formData.repeat === 'monthly') && (
                                <div className="animate-in slide-in-from-top-2 duration-300">
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                                        {formData.repeat === 'weekly' ? 'Select Days of Week' : 'Select Dates of Month'}
                                    </label>
                                    <Select
                                        isMulti
                                        options={formData.repeat === 'weekly' ? weekDays : monthDays}
                                        value={(formData.repeat === 'weekly' ? weekDays : monthDays).filter(d => (formData.repeat === 'weekly' ? formData.weekly : formData.monthly).includes(d.value))}
                                        onChange={(vals) => handleMultiSelect(formData.repeat === 'weekly' ? 'weekly' : 'monthly', vals)}
                                        className="text-sm react-select-container"
                                        classNamePrefix="react-select"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: base => ({ ...base, zIndex: 9999 })
                                        }}
                                    />
                                    <p className="mt-2 text-[10px] text-gray-400 italic">
                                        The ticket will be created on these specific {formData.repeat === 'weekly' ? 'days' : 'dates'} every {formData.repeat_interval} {formData.repeat === 'weekly' ? 'week(s)' : 'month(s)'}.
                                    </p>
                                </div>
                            )}

                            {/* Yearly Informational / Logic */}
                            {formData.repeat === 'yearly' && (
                                <div className="p-4 bg-red-50 border border-red-100 rounded-xl space-y-3 animate-in slide-in-from-top-2 duration-300">
                                    <div className="flex items-center gap-2 text-red-700 font-bold text-sm">
                                        <FiCalendar className="size-4" />
                                        Yearly Logic Confirmation
                                    </div>
                                    <p className="text-[11px] text-red-600 leading-relaxed font-medium">
                                        For long-term tracking (like your <strong>3-Year Certificate Expiry</strong>):
                                    </p>
                                    <ul className="text-[10px] text-red-500 space-y-1 ml-4 list-disc">
                                        <li>Current Interval: <strong>Every {formData.repeat_interval} Years</strong>.</li>
                                        <li>Trigger Date: The <strong>Month and Day</strong> of your selected Start Date.</li>
                                        <li>Example: If Start Date is 2026-03-03, it will trigger on March 3rd every {formData.repeat_interval} years.</li>
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div className="p-4 border-t bg-gray-50 flex justify-end">
                            <button
                                onClick={() => setIsRecurrencePopupOpen(false)}
                                className="px-6 py-2 bg-gray-900 text-white rounded-lg text-sm font-bold shadow-lg hover:bg-black transition-all active:scale-95"
                            >
                                Apply & Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TicketSchedulerModal;


